import PropTypes from "prop-types";
import { useState } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import ActionButton from "src/components/shared/ActionButton";
import { HOST_STEPS, codesOf } from "./decisions";
import { partyEvent } from "./partyWords";
import { dateLong, dateTime, money, statusLabel } from "./format";
import { moneyIn } from "./paymentWords";
import { partnerName, travellerLabel, travellerName, travellerNumber } from "./workflow";

// Emails about the filing, the draft and new copies, on the timeline.
const FILING_EMAILS = {
  copy_reminder: "Reminded the customer of the new copies",
  filing_update: "Emailed the customer where filing stands",
  appointment_reminder: "Reminded the customer of the appointment",
  fee_reminder: "Reminded the customer of a fee to pay",
  draft_reminder: "Reminded the customer of their draft",
};

// Payment changes a person makes arrive one per traveller: shown as one line
// when the same person made the same change within two minutes.
const SAME_ACTION_MS = 2 * 60 * 1000;

// When the current owner became the owner (the latest assignment to them),
// or null when it wasn't recorded (before migration 005, or assigned another
// way). For "Owner since" on the case (QA rania-14).
export function ownerSince(data) {
  if (!data?.assigned) return null;
  const event = [...(data.events || [])].reverse().find((e) => e.type === "assigned" && e.detail?.to === data.assigned.name);
  return event?.created_at || null;
}

// One line per thing that happened to the application, oldest first: the
// submission, payments, owners, status changes, document decisions, emails,
// visas, filing and the four visa decisions' events. The history always
// says submitted, paid, owner, status and document decisions (QA rania-14):
// what the events don't hold is worked out from the application's own dates
// where it can be, and otherwise listed at the end as "date not recorded",
// never made up.
export function timelineItems(data) {
  const partner = partnerName(data);
  const many = data.travellers.length > 1;
  const byRequest = new Map(data.travellers.map((t) => [t.request_id, t]));
  const of = (requestId) => {
    const t = byRequest.get(requestId);
    return many && t ? ` (${travellerName(t)}, ${travellerNumber(data, t)})` : "";
  };
  const history = data.timeline || [];
  const events = data.events || [];
  // A traveller moved on their own is named, with where they came from
  // ("Status of Maya Tester (1611-2): Draft → In review"; round 2, nadia-04).
  const byNumber = new Map(data.travellers.map((t, index) => [index + 1, t]));
  const items = history.map((e) => {
    const t = e.traveller ? byRequest.get(e.traveller.request_id) || byNumber.get(e.traveller.number) : null;
    const move = e.from_status && e.from_status !== e.status ? `${statusLabel(e.from_status)} → ${statusLabel(e.status)}` : statusLabel(e.status);
    return {
      at: e.created_at,
      title: t
        ? `Status of ${travellerName(t)} (${travellerNumber(data, t)}): ${move}`
        : e.status === "pending" && e.actor_type !== "staff"
          ? "Submitted"
          : `Status: ${statusLabel(e.status)}`,
      who: e.actor || (e.actor_type === "client" ? "Customer" : e.actor_type === "partner" ? partner || "Partner" : "System"),
      detail: e.reason,
    };
  });

  // The submission: the application's date is set when the customer
  // submits it (a draft's when it was started).
  if (data.status === "incomplete") {
    if (data.submitted_at) items.push({ at: data.submitted_at, title: "Started the application", who: partner || "Customer", derived: true });
  } else if (data.submitted_at && !history.some((e) => e.status === "pending")) {
    items.push({ at: data.submitted_at, title: "Submitted", who: partner || "Customer", derived: true });
  }

  // Paid: the bank's receipts when no payment event says it.
  const paidEvents = events.filter((e) => e.type === "payment" && ["confirmed", "marked"].includes(e.detail?.outcome));
  if (data.is_paid && !paidEvents.length) {
    const records = data.payment?.records || [];
    if (records.length) {
      records.forEach((r) =>
        items.push({ at: r.paid_at || r.created_at, title: `Paid online: ${moneyIn(r.amount, r.currency)}`, detail: r.receipt_number ? `Receipt ${r.receipt_number}` : undefined, derived: true }),
      );
    } else {
      items.push({ at: null, title: `Paid: ${money(data.payment?.amount ?? data.total)}`, detail: "When and how it was paid wasn’t recorded." });
    }
  }

  // The owner and the status, when no event says since when.
  if (data.assigned && !events.some((e) => e.type === "assigned")) {
    items.push({ at: null, title: `Owner: ${data.assigned.name}`, detail: "Since when wasn’t recorded." });
  }
  if (!["incomplete", "pending"].includes(data.status) && !history.some((e) => e.status === data.status && !e.traveller)) {
    items.push({ at: null, title: `Status: ${statusLabel(data.status)}`, detail: "When it changed wasn’t recorded." });
  }

  // Document decisions: every one from its event (approvals, new copies and
  // undos, with who); a review made before those events, from its date.
  const recorded = new Set(events.filter((e) => e.type === "document_review").map((e) => `${e.traveller_request_id}:${e.detail?.document_id}`));
  data.travellers.forEach((t) =>
    t.documents.forEach((d) => {
      const u = d.upload;
      if (!u?.reviewed_at || !u.review_status || recorded.has(`${t.request_id}:${d.document_id}`)) return;
      items.push({
        at: u.reviewed_at,
        title: `${u.review_status === "approved" ? "Approved" : "New copy needed"}: ${d.name}${of(t.request_id)}`,
        who: u.reviewed_by,
        detail: u.review_status === "needs_new" ? u.review_reason : undefined,
      });
    }),
  );

  // Codes the customer sent (migration 023): from their events, or from
  // the case's last five when the events don't carry them.
  if (!events.some((e) => e.type === "customer_code")) {
    codesOf(data).forEach((code) => items.push({ at: code.sent_at, title: "The customer sent a code", detail: code.text, mono: true, who: "Customer" }));
  }

  let lastPayment = null;
  events.forEach((e) => {
    const d = e.detail || {};
    // A traveller's own status change: its status-history line above says it.
    if (e.type === "status") return;
    const item = { at: e.created_at, who: e.actor };
    // A host's or sponsor's link and papers (API migration 031).
    const party = partyEvent(e.type, d);
    if (party) Object.assign(item, party, { who: party.who ? e.actor || party.who : e.actor });
    else if (e.type === "assigned") item.title = d.to ? `Assigned to ${d.to}` : "Owner removed";
    else if (e.type === "document_review") {
      const name = `${d.document || "A document"}${of(e.traveller_request_id)}`;
      if (d.decision === "approved") item.title = `Approved: ${name}`;
      else if (d.decision === "needs_new") {
        item.title = `New copy needed: ${name}`;
        item.detail = d.reason;
      } else item.title = d.previous === "approved" ? `Approval undone: ${name}` : d.previous === "needs_new" ? `New copy request undone: ${name}` : `Review cleared: ${name}`;
    } else if (e.type === "customer_code") {
      item.title = "The customer sent a code";
      item.detail = d.text;
      item.mono = true;
      item.who = e.actor || "Customer";
    } else if (e.type === "customer_code_used") item.title = "Used the customer’s code";
    else if (e.type === "dates_intended") {
      // "This is intended" on a date warning before paying (DE-02).
      const names = Array.isArray(d.names) && d.names.length ? d.names.join(", ") : "a document";
      item.title = `The customer says the dates are intended: ${names}${of(e.traveller_request_id)}`;
      item.detail = d.note || undefined;
      item.who = e.actor || "Customer";
    } else if (e.type === "document_reused") {
      // A copy used again from an earlier application (SA-09).
      item.title = `Used again from application #${d.from_application ?? "?"}: ${d.document || "a document"}${of(e.traveller_request_id)}`;
      item.who = e.actor || "Customer";
    }
    else if (e.type === "customer_called") item.title = "Called the customer about the refusal";
    else if (e.type === "fee_paid_by_customer") {
      item.title = `The customer says they paid${d.step || d.title ? `: ${d.step || d.title}` : " a fee"}`;
      item.who = e.actor || "Customer";
    } else if (e.type === "host_status") {
      const step = HOST_STEPS.find((h) => h.status === d.status);
      item.title = `Host: ${step ? step.label.toLowerCase() : d.status || "updated"}`;
      // A tracking number when one was typed; a bare "true" said nothing (nadia-15).
      const number = typeof d.tracking === "string" && d.tracking.trim() ? d.tracking.trim() : d.tracking === true && data.host?.tracking ? String(data.host.tracking) : null;
      item.detail = number ? `Tracking number ${number}` : d.tracking === true ? "With a tracking number" : undefined;
      item.who = e.actor || "Host";
    } else if (e.type === "filing") {
      item.title =
        d.action === "started"
          ? `Filing started${d.steps ? ` (${d.steps} steps)` : ""}`
          : d.action === "done"
            ? `Done: ${d.step || "a step"}`
            : `Not done after all: ${d.step || "a step"}`;
    } else if (e.type === "email" && FILING_EMAILS[d.kind]) {
      item.title = d.sent ? FILING_EMAILS[d.kind] : `Email failed: ${FILING_EMAILS[d.kind].replace(/^\w/, (c) => c.toLowerCase())}`;
      item.failed = !d.sent;
    } else if (e.type === "note") {
      // A team note corrected or removed by its writer (the words aren't kept).
      item.title = d.action === "deleted" ? "Deleted a team note" : "Edited a team note";
    } else if (e.type === "email" && d.kind === "assigned") {
      // Claiming a submitted case emails the customer who is reviewing it.
      item.title = d.sent ? `Emailed the customer: ${d.to || "a visa agent"} is reviewing it` : "Email failed: who is reviewing it";
      item.failed = !d.sent;
    } else if (e.type === "email" && d.kind === "together_refund") {
      item.title = d.sent ? "Emailed the customer: their invite’s 10% is on its way back" : "Not emailed: their invite’s 10% is on its way back";
      item.failed = !d.sent;
    } else if (e.type === "together") {
      // "Travelling together" (API docs/travelling-together.md).
      const amount = d.amount != null ? money(d.amount) : "10% of Safrati’s fee";
      item.title =
        {
          pending: `Someone they invited paid (application ${d.from_application}): ${amount} off their own checkout`,
          refund_due: `Someone they invited paid (application ${d.from_application}): refund ${amount} to them`,
          refunded: `Refunded ${amount} to the customer for their invite`,
        }[d.outcome] || "Travelling together";
    } else if (e.type === "email" && d.kind === "new_copies") {
      item.title = d.sent ? "Emailed the customer for new copies" : "Email for new copies failed";
      item.failed = !d.sent;
    } else if (e.type === "email") {
      item.title = d.sent ? `Emailed the customer: ${statusLabel(d.status)}` : `Email failed: status ${statusLabel(d.status)}`;
      item.failed = !d.sent;
    } else if (e.type === "partner") {
      // Delivered to the partner platform, which tells its traveller.
      const to = d.partner || "the partner";
      if (d.failed) {
        item.title = `${to} could not be reached`;
        item.detail = d.error;
        item.failed = true;
      } else
        item.title =
          {
            "application.status_changed": `Sent to ${to}: ${statusLabel(d.status)}`,
            "traveller.documents_requested": `Asked ${to} for new copies`,
            "traveller.visa_issued": `Visa sent to ${to}`,
          }[d.event] || `Sent to ${to}`;
    } else if (e.type === "visa_sent") {
      const t = byRequest.get(e.traveller_request_id);
      // On a partner's case this is the upload; the partner event that follows
      // says it was delivered.
      item.title = `${d.partner ? "Visa uploaded" : "Visa sent"}${many && t ? ` for ${travellerLabel(data, t)}` : ""}`;
      // emailed is null on a partner's case: no email was due, nothing failed.
      if (d.emailed === false) {
        item.detail = "Saved in the customer’s account; the email was not sent.";
        item.failed = true;
      }
    } else if (e.type === "payment" && (d.outcome === "marked" || d.outcome === "unmarked")) {
      const at = new Date(e.created_at).getTime();
      if (lastPayment && lastPayment.outcome === d.outcome && lastPayment.who === e.actor && at - lastPayment.time <= SAME_ACTION_MS) {
        lastPayment.item.count += 1;
        lastPayment.time = at;
        return;
      }
      item.count = 1;
      item.outcome = d.outcome;
      item.title =
        d.outcome === "marked"
          ? `Marked as paid${[d.method, d.reference && `ref ${d.reference}`].filter(Boolean).length ? ` (${[d.method, d.reference && `ref ${d.reference}`].filter(Boolean).join(", ")})` : ""}`
          : "Marked as not paid";
      if (d.outcome === "marked" && d.paid_on) item.detail = `Paid on ${dateLong(d.paid_on)}`;
      lastPayment = { outcome: d.outcome, who: e.actor, time: at, item };
    } else if (e.type === "payment") {
      item.title =
        {
          confirmed: `Bank confirmed the payment${d.amount != null ? ` of ${money(d.amount)}` : ""}${d.by === "check" ? " (found when the customer reopened the application)" : ""}`,
          not_confirmed: "Bank: no completed payment",
          unreachable: "Bank could not be reached",
        }[d.outcome] || "Payment";
      item.failed = d.outcome === "not_confirmed" || d.outcome === "unreachable";
    } else item.title = e.type;
    items.push(item);
  });
  items.forEach((item) => {
    if (item.count > 1) item.title = `${item.title} · ${item.count} travellers`;
  });
  const dated = items.filter((item) => item.at && !Number.isNaN(new Date(item.at).getTime())).sort((x, y) => new Date(x.at) - new Date(y.at));
  const undated = items.filter((item) => !dated.includes(item)).map((item) => ({ ...item, undated: true }));
  return [...dated, ...undated];
}

const TIMELINE_SHOWN = 8;

export default function Timeline({ data }) {
  const [all, setAll] = useState(false);
  const items = timelineItems(data);
  const shown = all ? items : items.slice(-TIMELINE_SHOWN);
  return (
    <Card variant="outlined" id="case-timeline" tabIndex={-1} sx={{ outline: "none", scrollMarginTop: 180 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
          <Typography variant="h6" component="h2">
            Timeline
          </Typography>
          {items.length > TIMELINE_SHOWN && (
            <ActionButton variant="text" size="small" onClick={() => setAll((v) => !v)} aria-expanded={all}>
              {all ? "Show the latest" : `Show all ${items.length}`}
            </ActionButton>
          )}
        </Stack>
        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nothing recorded yet.
          </Typography>
        )}
        <Stack spacing={1.5} component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {shown.map((item, index) => (
            <Box component="li" key={`${item.at}-${index}`}>
              <Typography variant="body2" fontWeight={600} color={item.failed ? "warning.dark" : "text.primary"}>
                {item.title}
                {item.who && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {" "}
                    · {item.who}
                  </Typography>
                )}
              </Typography>
              {item.detail && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={item.mono ? { fontFamily: "monospace", fontWeight: 600, overflowWrap: "anywhere" } : { overflowWrap: "anywhere" }}
                >
                  {item.detail}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {item.undated ? "Date not recorded" : `${dateTime(item.at)}${item.derived ? " (from the application’s dates)" : ""}`}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

Timeline.propTypes = { data: PropTypes.object.isRequired };
